import React, { useState } from 'react'

import {
  AttendanceSidepanelCompensationBlockFragment,
  BulkCreateAttendanceTimeBlockCompensationMutation,
  PayrollConceptFragment,
  ReimbursablePayrollConceptFragment,
  TimeSettingsCustomTimeRangeCategoryFragment,
} from 'generated/resources/graphql'
import { isEmpty } from 'lodash'
import moment from 'moment'

import { track, trackingEvents } from 'modules/core/lib/Tracking'

import PayrollConceptMapping from 'modules/attendance/components/ClockIn/EmployeeTimeTrackingBreakdown/Compensation/Modals/PayrollConceptMapping'
import PayrollPeriodSelection from 'modules/attendance/components/ClockIn/EmployeeTimeTrackingBreakdown/Compensation/Modals/PayrollPeriodSelection'
import TimeBlocks from 'modules/attendance/components/ClockIn/EmployeeTimeTrackingBreakdown/Compensation/Modals/TimeBlocks'
import { useAttendanceDateNavigator } from 'modules/attendance/lib/attendanceDateNavigatorContext'
import { timeRangeCategoryTypeEnum } from 'modules/attendance/lib/enums'
import TimeBlocksSupplementForm from 'modules/attendance/lib/forms/TimeBlocksSupplementForm'
import type { CompensationBlockAllocationInput } from 'modules/attendance/lib/types'
// TODO: this is not the store importation, it is an exported class from the store
import { TimeBlocksBalanceCollection } from 'modules/attendance/store/models/TimeBlocksBalanceCollection'
import { UniqueTimeBlockData } from 'modules/attendance/store/models/UniqueTimeBlockData'

type AttendanceManualCompensation = NonNullable<
  NonNullable<
    BulkCreateAttendanceTimeBlockCompensationMutation['attendanceMutations']['bulkCreateTimeBlockSupplementAttendanceManualCompensation']
  >['manualCompensation']
>

interface Props {
  employeeId: number
  getResponseData: (response: AttendanceManualCompensation | undefined | null) => void
  loading: boolean
  onCancel: () => void
  timeBlocksBalance?: TimeBlocksBalanceCollection
  timeSheetDate: moment.Moment
  reimbursements: ReimbursablePayrollConceptFragment[]
  timeRangeCategories: TimeSettingsCustomTimeRangeCategoryFragment[]
  payrollConcepts: PayrollConceptFragment[]
  selectedCompensationBlocks: AttendanceSidepanelCompensationBlockFragment[]
  /** Legacy: array of block IDs. Used when FF DEV_TIME_TRACKING_MANUAL_ALLOCATION is disabled. */
  compensationBlockIds?: string[]
  /** New: structured allocation payload. Used when FF DEV_TIME_TRACKING_MANUAL_ALLOCATION is enabled. */
  compensationBlocks?: CompensationBlockAllocationInput[]
}

const WIZARD_STEPS = {
  TIME_BLOCKS: 1,
  PAYROLL_PERIOD: 2,
  MAP_TIME_CATEGORY_WITH_PAYROLL_CONCEPT: 3,
} as const

const CreateTimeBlockCompensationWizard = ({
  employeeId,
  getResponseData,
  loading,
  onCancel,
  timeBlocksBalance,
  reimbursements,
  timeRangeCategories,
  payrollConcepts,
  selectedCompensationBlocks = [],
  compensationBlockIds,
  compensationBlocks,
}: Props) => {
  const { stringDateFrom, stringDateTo } = useAttendanceDateNavigator()

  // Clamp endOn to yesterday only if the period extends into the future
  const today = moment()
  const periodEnd = moment(stringDateTo)
  const startOn = stringDateFrom
  const endOn = periodEnd.isSameOrAfter(today, 'day')
    ? today.clone().subtract(1, 'day').format('YYYY-MM-DD')
    : stringDateTo

  const [selectedTimeBlocks, setSelectedTimeBlocks] = useState<UniqueTimeBlockData[]>([])

  const usingCompensationBlocks = selectedCompensationBlocks.length > 0

  const initialStep = usingCompensationBlocks ? 2 : 1

  const [currentStep, setCurrentStep] = useState<number | undefined>(initialStep)

  const form = TimeBlocksSupplementForm({
    setResponseData: getResponseData,
    employeeId: +employeeId,
    startOn: timeBlocksBalance?.startOn || startOn,
    endOn: timeBlocksBalance?.endOn || endOn,
    compensationBlockIds,
    compensationBlocks,
  })

  const selectedCategoryIds = usingCompensationBlocks
    ? selectedCompensationBlocks.map((cb) => cb.categoryId)
    : selectedTimeBlocks.map((tb) => tb.timeSettingsCustomTimeRangeCategoryId)

  const selectedCategoryIdsSet = new Set(selectedCategoryIds)

  const timeCategoriesWithPending = timeRangeCategories.filter((category) =>
    selectedCategoryIdsSet.has(category.id)
  )

  const hasAllCategoryMappings = timeCategoriesWithPending.every(
    (category) =>
      reimbursements.find(
        (r) =>
          r.reimbursableId === category.id &&
          r.reimbursableType === timeRangeCategoryTypeEnum.TimeSettingsCustomTimeRangeCategory
      )?.payrollConceptId
  )

  const handleTimeBlockSelection = (timeBlock: UniqueTimeBlockData, checked: boolean) => {
    const updated = checked
      ? [...selectedTimeBlocks, timeBlock]
      : selectedTimeBlocks.filter((t) => t.name !== timeBlock.name)

    setSelectedTimeBlocks(updated)
    form.setValues({ ...form.values, timeBlocks: updated })
  }

  const handleTimeBlocksContinue = () => {
    track(trackingEvents.timeTracking.compensatingCategories, {
      selectedTimeBlocks: selectedTimeBlocks.map((tb) => tb.name || 'balance'),
    })
    track(trackingEvents.compensations.createCompensation, {
      source: 'timesettings_customtimerangecategory',
      selectedTimeBlocks: selectedTimeBlocks.map((tb) => tb.name || 'balance'),
    })
    setCurrentStep(WIZARD_STEPS.PAYROLL_PERIOD)
  }

  const handlePayrollPeriodContinue = () =>
    setCurrentStep(WIZARD_STEPS.MAP_TIME_CATEGORY_WITH_PAYROLL_CONCEPT)

  const handleFinalStep = () => {
    form.handleSubmit()

    if (isEmpty(form.errors)) {
      track(trackingEvents.timeTracking.compensatingCategoriesFinish, {
        selectedTimeBlocks: selectedTimeBlocks.map((tb) => tb.name || 'balance'),
        period: form.values.period,
      })

      setCurrentStep(undefined)
      onCancel()
    }
  }

  const renderStep = () => {
    if (currentStep === WIZARD_STEPS.TIME_BLOCKS) {
      if (!timeBlocksBalance) {
        return null
      }
      return (
        <TimeBlocks
          onSelectTimeBlock={handleTimeBlockSelection}
          onCloseModal={onCancel}
          onContinue={handleTimeBlocksContinue}
          disableButton={selectedTimeBlocks.length === 0}
          timeBlocksLoading={loading}
          timeBlocksBalance={timeBlocksBalance}
        />
      )
    }

    if (currentStep === WIZARD_STEPS.PAYROLL_PERIOD) {
      return (
        <PayrollPeriodSelection
          form={form}
          onCancel={onCancel}
          onSubmit={hasAllCategoryMappings ? handleFinalStep : handlePayrollPeriodContinue}
          hasMissingCategoriesMappings={!hasAllCategoryMappings}
        />
      )
    }

    if (currentStep === WIZARD_STEPS.MAP_TIME_CATEGORY_WITH_PAYROLL_CONCEPT) {
      return (
        <PayrollConceptMapping
          reimbursements={reimbursements}
          payrollConcepts={payrollConcepts}
          timeRangeCategories={timeCategoriesWithPending}
          onCancel={onCancel}
          onSubmit={handleFinalStep}
        />
      )
    }

    return null
  }

  return <>{renderStep()}</>
}

export default CreateTimeBlockCompensationWizard
